// Stato della connessione Eureka + riconnessione OAuth senza script locali.
//
// Scelte, in breve:
// - NON è una card. È stato + una singola azione: una card sarebbe un
//   contenitore che non porta informazione (register product: le card sono la
//   risposta pigra). Il connesso vive come pill nella toolbar, il disconnesso
//   come striscia nello slot di stato della pagina (lo stesso di `syncMsg`).
// - Divulgazione progressiva: quando è tutto a posto occupa pochi pixel;
//   diventa evidente SOLO quando è rotto, che è l'unico momento in cui serve
//   un'azione. Un banner permanente su uno stato sano è rumore.
// - Se la funzione è spenta (`enabled=false`) non renderizza nulla.
// - Contrasto: `text-ink` su `bg-<colore>-soft`. La coppia `text-<colore>` sul
//   proprio `-soft` è sotto 4.5:1 ed è RIFIUTATA da `shared/contrast.test.ts`:
//   la tinta piena vive solo nel pallino, che non è testo.
import { useCallback, useEffect, useState } from "react";

import { apiGet, apiPost, prettifyApiError } from "@/shared/api";

type EurekaStatus = {
  enabled: boolean;
  connected: boolean;
  connected_at: string | null;
};

const PILL = "inline-flex items-center gap-1.5 text-[12px] transition-colors duration-150";

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function EurekaConnection() {
  const [status, setStatus] = useState<EurekaStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await apiGet<EurekaStatus>("/api/invoicing/eureka/status"));
    } catch {
      // Lo stato è accessorio: se non si legge, la pagina resta usabile e non
      // mostriamo nulla invece di un errore che l'utente non può risolvere.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const riconnetti = useCallback(async () => {
    setError(null);
    setBusy(true);
    // La scheda va aperta SINCRONAMENTE al click: aprirla dopo l'await la fa
    // bloccare dal popup blocker (non è più gesto utente). Apriamo vuota e le
    // diamo l'URL quando arriva.
    // NIENTE `noopener`/`noreferrer` qui: con quelli window.open ritorna SEMPRE
    // null per specifica (è il loro scopo: nessun handle), il ramo `if (scheda)`
    // era codice morto e si finiva sempre nel fallback, che porta via l'app
    // dalla scheda corrente. La protezione anti tabnabbing resta: disconosciamo
    // l'opener a mano, mentre la scheda è ancora vuota.
    const scheda = window.open("", "_blank");
    if (scheda) scheda.opener = null;
    try {
      const { authorize_url } = await apiPost<{ authorize_url: string }>(
        "/api/invoicing/eureka/oauth/start",
      );
      if (scheda) {
        scheda.location.href = authorize_url;
      } else {
        // Popup bloccato: non lasciare l'utente a mani vuote.
        window.location.href = authorize_url;
      }
    } catch (err) {
      scheda?.close();
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  // Finché non sappiamo, niente: nessuno scheletro per due parole di stato.
  if (!status?.enabled) return null;

  const azione = (
    <button
      type="button"
      onClick={() => void riconnetti()}
      disabled={busy}
      className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-full border border-line-2 bg-card px-3.5 text-[12.5px] font-semibold text-ink transition-colors hover:border-ink-4 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
    >
      {busy ? "Apro Eureka…" : "Riconnetti Eureka"}
    </button>
  );

  if (!status.connected) {
    return (
      <div
        role="status"
        className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-amber-soft px-3 py-2.5"
      >
        <div>
          <p className="text-[12.5px] text-ink">
            <span aria-hidden className="mr-1.5 inline-block size-2 rounded-full bg-amber" />
            <strong className="font-semibold">Eureka non è connesso.</strong> Il registro non si
            aggiorna e la riconciliazione dei documenti resta ferma.
          </p>
          {error && <p className="mt-1 text-[12px] text-red">{error}</p>}
        </div>
        {azione}
      </div>
    );
  }

  const dal = formatDate(status.connected_at);
  return (
    <div className="mb-3 flex items-center gap-2">
      <span
        className={`${PILL} text-ink-2`}
        title={dal ? `Riconnesso il ${dal}` : "Connessione Eureka attiva"}
      >
        <span aria-hidden className="inline-block size-2 rounded-full bg-green" />
        Eureka connesso
      </span>
      <button
        type="button"
        onClick={() => void riconnetti()}
        disabled={busy}
        className="text-[12px] text-accent transition-opacity duration-150 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50 disabled:no-underline"
      >
        {busy ? "Apro…" : "Riconnetti"}
      </button>
      {error && <span className="text-[12px] text-red">{error}</span>}
    </div>
  );
}
